/**
 * Phase 25 Plan 25-03 D-06 — caret-coordinate measurement.
 *
 * Strategy: render an invisible <div> mirror of the textarea (same
 * width, font, padding, border), copy the textarea's text up to the
 * caret position, append a marker <span>, then read the span's
 * offsetTop / offsetLeft. The textarea's scrollTop / scrollLeft is
 * subtracted so the popover stays anchored as the user scrolls within
 * the textarea.
 *
 * Adapted from textarea-caret-position (MIT) — minimal subset without
 * DOM cleanup race conditions for our single-textarea use case (the
 * mirror div is created and removed inside one synchronous call).
 */
export interface CaretCoords {
  top: number;
  left: number;
  height: number;
}

const MIRRORED_PROPS = [
  'boxSizing',
  'width',
  'height',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
  'MozTabSize',
] as const;

export function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  position: number,
): CaretCoords {
  const div = document.createElement('div');
  document.body.appendChild(div);

  const style = div.style;
  const computed = window.getComputedStyle(textarea);

  style.whiteSpace = 'pre-wrap';
  style.wordWrap = 'break-word';
  style.position = 'absolute';
  style.visibility = 'hidden';

  // CSSStyleDeclaration includes read-only members (`length`, `parentRule`)
  // that TS widens into the index signature, so writing through
  // `keyof CSSStyleDeclaration` is rejected. Mirroring writable string
  // properties via a string index signature is type-safe at runtime.
  const writableStyle = style as unknown as Record<string, string>;
  const computedRecord = computed as unknown as Record<string, string>;
  for (const prop of MIRRORED_PROPS) {
    writableStyle[prop] = computedRecord[prop] ?? '';
  }

  div.textContent = textarea.value.substring(0, position);
  const span = document.createElement('span');
  // Append a non-empty placeholder so the span has measurable dimensions
  // even if the caret is at the end of the value.
  span.textContent = textarea.value.substring(position) || '.';
  div.appendChild(span);

  const borderTop = parseInt(computed.borderTopWidth, 10) || 0;
  const borderLeft = parseInt(computed.borderLeftWidth, 10) || 0;
  const lineHeight = parseInt(computed.lineHeight, 10);

  const coords: CaretCoords = {
    top: span.offsetTop + borderTop - textarea.scrollTop,
    left: span.offsetLeft + borderLeft - textarea.scrollLeft,
    height: Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 16,
  };

  document.body.removeChild(div);
  return coords;
}
